import React, { useState } from "react";
import { X, Download, Loader2 } from "lucide-react";
import { useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
export default function AllergenReferenceMenu({
  isOpen,
  onClose,
  items = [],
  restaurantName = "All Restaurants",
  selectedItems = [],
  onFetchAllItems,
}) {
  const [isExporting, setIsExporting] = useState(false);
  const itemsToShow = useMemo(() => {
    if (selectedItems && selectedItems.length > 0) {
      return items.filter((i) => selectedItems.includes(i.uuid || i.id));
    }
    return items;
  }, [items, selectedItems]);

  const restaurantDisplayName = useMemo(() => {
    if (restaurantName && restaurantName !== "All Restaurants") {
      return restaurantName;
    }
    if (itemsToShow.length > 0) {
      const firstItem = itemsToShow[0];
      return (
        firstItem.restaurantname ||
        firstItem.restaurantNameDisplay ||
        firstItem.restaurant_name ||
        firstItem.restaurantName ||
        restaurantName
      );
    }
    return restaurantName;
  }, [restaurantName, itemsToShow]);

  // Map dynamic items to component structure
  const foodItems = itemsToShow.map((item) => {
    const dietary = item.dietary_restrictions || {};

    // Handle price for both Food and Wine
    let displayPrice = item.price;
    if (displayPrice === undefined && item.offering) {
      displayPrice =
        item.offering.glass_price || item.offering.bottle_price || 0;
    }

    return {
      dish_name: item.name || item.product_name || "Unknown Item",
      category:
        item.typeDisplay ||
        (Array.isArray(item.type) ? item.type[0] : null) ||
        item.category ||
        "Other",
      allergens: Array.isArray(item.allergens) ? item.allergens : [],
      health_considerations: Array.isArray(dietary.health)
        ? dietary.health
        : [],
      belief_accommodations: Array.isArray(dietary.belief)
        ? dietary.belief
        : [],
      dietary_preferences: Array.isArray(dietary.lifestyle)
        ? dietary.lifestyle
        : [],
      accommodations: Array.isArray(item.accommodations)
        ? item.accommodations
        : [],
      price: displayPrice || 0,
    };
  });

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF();
      let itemsToExport = items;

      if (onFetchAllItems) {
        try {
          const fetched = await onFetchAllItems();
          // Ensure we get an array
          if (fetched && Array.isArray(fetched) && fetched.length > 0) {
            itemsToExport = fetched;
          } else if (fetched && fetched.data && Array.isArray(fetched.data)) {
            itemsToExport = fetched.data;
          }
        } catch (err) {
          console.error("Error fetching all items:", err);
          // Fallback to current items
        }
      }

      // Sort items: Primary Category (A-Z), Secondary Name (A-Z)
      const sortedItems = [...itemsToExport].sort((a, b) => {
        const catA = (a.typeDisplay || (Array.isArray(a.type) ? a.type[0] : null) || a.category || "Other").toLowerCase();
        const catB = (b.typeDisplay || (Array.isArray(b.type) ? b.type[0] : null) || b.category || "Other").toLowerCase();

        if (catA < catB) return -1;
        if (catA > catB) return 1;

        const nameA = (a.name || a.product_name || "").toLowerCase();
        const nameB = (b.name || b.product_name || "").toLowerCase();

        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;

        return 0;
      });

      // Map items using the same logic as render
      const allData = sortedItems.map((item, index) => {
        const dietary = item.dietary_restrictions || {};
        let displayPrice = item.price;
        if (displayPrice === undefined && item.offering) {
          displayPrice =
            item.offering.glass_price || item.offering.bottle_price || 0;
        }

        return [
          index + 1,
          item.name || item.product_name || "Unknown Item",
          item.typeDisplay ||
          (Array.isArray(item.type) ? item.type[0] : null) ||
          item.category ||
          "Other",
          Array.isArray(item.allergens) ? item.allergens.join(", ") : "",
          Array.isArray(dietary.health) ? dietary.health.join(", ") : "",
          Array.isArray(dietary.belief) ? dietary.belief.join(", ") : "",
          Array.isArray(dietary.lifestyle) ? dietary.lifestyle.join(", ") : "",
          Array.isArray(item.accommodations)
            ? item.accommodations.join(", ")
            : "",
          `$${Number(displayPrice || 0).toFixed(2)}`,
        ];
      });

      autoTable(doc, {
        head: [
          [
            "ID",
            "Dish",
            "Category",
            "Allergens",
            "Health",
            "Belief",
            "Lifestyle",
            "Accommodations",
            "Price",
          ],
        ],
        body: allData,
        theme: "striped",
        headStyles: { fillColor: [194, 65, 12] }, // Match the orange/red theme #C2410C
        styles: { fontSize: 8 },
        startY: 20,
        margin: { top: 25 },
        didDrawPage: function (data) {
          // Header
          doc.setFontSize(16);
          doc.text("Allergen Reference Menu", 14, 15);
        },
      });

      doc.save("allergen-reference-menu.pdf");
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const content = (
    <div
      className={`bg-[#F9F9F9] w-full ${isOpen ? "max-w-[1400px] h-[90vh] shadow-xl rounded-lg" : "h-full"
        } overflow-hidden flex flex-col relative`}
    >
      {/* Close Button Only if Modal */}
      {isOpen && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-sm hover:bg-gray-50"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      )}

      {/* Header Section */}
      <div
        className={`px-4 pt-4 pb-2 bg-[#F9F9F9] ${!isOpen && "border-b border-gray-100"
          }`}
      >
        <div className="flex justify-between items-start mb-3">
          <div>
            <h1 className="text-lg text-start font-bold text-gray-900">
              Allergens, Restrictions, and Accommodations Menu
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Allergens, Health/Belief/Lifestyle Restrictions & Accommodations •
              Restaurant: {restaurantDisplayName}
            </p>
          </div>
          <div className="text-right">
            <h2 className="text-sm font-bold text-gray-900">
              {restaurantDisplayName}
            </h2>
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">
              Speak Your Menu
            </p>
          </div>
        </div>

        {/* Legend/Key and Export */}
        <div className="flex items-center gap-3">
          <div className="flex-1 flex items-center gap-4 py-2 px-3 bg-white rounded-md border border-gray-100 shadow-sm overflow-x-auto">
            <div className="flex items-center gap-1.5 min-w-max">
              <div className="w-2.5 h-2.5 rounded-full bg-[#FFF1E6]"></div>
              <span className="text-[10px] font-medium text-gray-600">
                Category
              </span>
            </div>
            <div className="flex items-center gap-1.5 min-w-max">
              <div className="w-2.5 h-2.5 rounded-sm bg-[#FDECEC]"></div>
              <span className="text-[10px] font-medium text-gray-600">
                Allergens
              </span>
            </div>
            <div className="flex items-center gap-1.5 min-w-max">
              <div className="w-2.5 h-2.5 rounded-sm bg-[#EEF4FF]"></div>
              <span className="text-[10px] font-medium text-gray-600">
                Health
              </span>
            </div>
            <div className="flex items-center gap-1.5 min-w-max">
              <div className="w-2.5 h-2.5 rounded-sm bg-gray-100"></div>
              <span className="text-[10px] font-medium text-gray-600">
                Belief
              </span>
            </div>
            <div className="flex items-center gap-1.5 min-w-max">
              <div className="w-2.5 h-2.5 rounded-sm bg-[#ECFDF3]"></div>
              <span className="text-[10px] font-medium text-gray-600">
                Lifestyle
              </span>
            </div>
            <div className="flex items-center gap-1.5 min-w-max">
              <div className="w-2.5 h-2.5 rounded-sm bg-[#FEF3C7]"></div>
              <span className="text-[10px] font-medium text-gray-600">
                Accommodations
              </span>
            </div>
          </div>

          <button
            onClick={handleExportPDF}
            disabled={isExporting}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-md shadow-sm hover:bg-gray-50 transition-colors text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Download className="w-3.5 h-3.5" />
            )}
            {isExporting ? "Exporting..." : "Export PDF"}
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="flex-1 overflow-auto px-4 pb-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#FFF9F5] border-b border-[#FDE6D2]">
              <tr>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-[20%]">
                  Dish
                </th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-[10%]">
                  Category
                </th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-[12%]">
                  Allergens
                </th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-[12%]">
                  Health Restrictions
                </th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-[10%]">
                  Belief
                </th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-[10%]">
                  Lifestyle
                </th>
                <th className="py-2 px-2 text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-[14%]">
                  Accommodations
                </th>
                <th className="py-2 px-2 text-right text-[9px] font-bold text-gray-700 uppercase tracking-tighter w-[8%]">
                  Price
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {foodItems.map((item, index) => (
                <tr
                  key={index}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  {/* Dish Name */}
                  <td className="py-2 px-2 text-[9px] font-bold text-gray-900 align-top">
                    {item.dish_name || item.name}
                  </td>

                  {/* Category */}
                  <td className="py-2 px-2 align-top">
                    <span className="inline-block px-1.5 py-0.5 bg-[#FFF1E6] text-[#C2410C] text-[8px] font-bold rounded">
                      {item.category || "Entrée"}
                    </span>
                  </td>

                  {/* Allergens */}
                  <td className="py-2 px-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {item.allergens && item.allergens.length > 0 ? (
                        item.allergens.map((allergen, i) => (
                          <span
                            key={i}
                            className="inline-block px-1.5 py-0.5 bg-[#FDECEC] text-[#B42318] text-[8px] font-bold rounded"
                          >
                            {allergen}
                          </span>
                        ))
                      ) : (
                        <span className="text-[8px] text-gray-400">None</span>
                      )}
                    </div>
                  </td>

                  {/* Health Restrictions */}
                  <td className="py-2 px-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {item.health_considerations &&
                        item.health_considerations.length > 0 ? (
                        item.health_considerations.map((health, i) => (
                          <span
                            key={i}
                            className="inline-block px-1.5 py-0.5 bg-[#EEF4FF] text-[#1D4ED8] text-[8px] font-bold rounded"
                          >
                            {health}
                          </span>
                        ))
                      ) : (
                        <span className="text-[8px] text-gray-400">None</span>
                      )}
                    </div>
                  </td>

                  {/* Belief */}
                  <td className="py-2 px-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {item.belief_accommodations &&
                        item.belief_accommodations.length > 0 ? (
                        Array.isArray(item.belief_accommodations) ? (
                          item.belief_accommodations.map((belief, i) => (
                            <span
                              key={i}
                              className="text-[8px] text-gray-500 font-medium"
                            >
                              {belief}
                            </span>
                          ))
                        ) : (
                          <span className="text-[8px] text-gray-500 font-medium">
                            {item.belief_accommodations}
                          </span>
                        )
                      ) : (
                        <span className="text-[8px] text-gray-400">None</span>
                      )}
                    </div>
                  </td>

                  {/* Lifestyle */}
                  <td className="py-2 px-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {item.dietary_preferences &&
                        item.dietary_preferences.length > 0 ? (
                        item.dietary_preferences.map((diet, i) => (
                          <span
                            key={i}
                            className="inline-block px-1.5 py-0.5 bg-[#ECFDF3] text-[#027A48] text-[8px] font-bold rounded"
                          >
                            {diet}
                          </span>
                        ))
                      ) : (
                        <span className="text-[8px] text-gray-400">None</span>
                      )}
                    </div>
                  </td>

                  {/* Accommodations */}
                  <td className="py-2 px-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {item.accommodations && item.accommodations.length > 0 ? (
                        item.accommodations.map((acc, i) => (
                          <span
                            key={i}
                            className="inline-block px-1.5 py-0.5 bg-[#FFFBEB] text-[#B45309] text-[8px] font-bold rounded"
                          >
                            {acc}
                          </span>
                        ))
                      ) : (
                        <span className="text-[8px] text-gray-400">None</span>
                      )}
                      {/* {item.accommodations && item.accommodations.length > 2 && (
                             <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-md">+{item.accommodations.length - 2}</span>
                        )} */}
                    </div>
                  </td>

                  {/* Price */}
                  <td className="py-2 px-2 text-right text-[9px] font-bold text-gray-900 align-top">
                    ${Number(item.price || 0).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="px-8 py-4 bg-[#F9F9F9] border-t border-gray-200 flex justify-between items-center text-[10px] text-gray-400">
        <div>
          For training use only • Last updated from Speak Your Menu data sync on{" "}
          {new Date().toISOString().split("T")[0]}
        </div>
      </div>
    </div>
  );

  if (isOpen) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        {content}
      </div>
    );
  }

  return content;
}
